import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ApiError, SESSION_EXPIRED_EVENT, loginRequest } from '../lib/api';
import { useAuthStore } from '../lib/auth';

export function ReAuthModal(): JSX.Element | null {
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const currentUsername = useAuthStore((s) => s.user?.username ?? '');

  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onSessionExpired(): void {
      setOpen((current) => {
        if (current) {
          return current;
        }
        clearAuth();
        setUsername(currentUsername);
        setPassword('');
        setError('Session expired. Please re-enter your credentials.');
        return true;
      });
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [clearAuth, currentUsername]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const data = await loginRequest(username, password);
      setAuth(data.user, data.csrfToken, [...data.permissions]);
      setOpen(false);
      void queryClient.invalidateQueries();
    } catch (err) {
      const fallback = 'Authentication failed. Please try again.';
      setError(err instanceof ApiError ? (err.messages[0]?.message ?? fallback) : fallback);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>Session expired</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Please re-enter your credentials to continue.
            </Typography>

            <TextField
              label="Username"
              type="text"
              name="username"
              autoComplete="username"
              required
              disabled={submitting}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              disabled={submitting}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />

            {error !== null ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Signing in…' : 'Continue'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
